import { useEffect, useState } from 'react';
import {
  resolveFieldDefault,
  resolveStaticOptions,
  type Country,
  type FieldDef,
  type FieldOption,
} from '@jfh/intake-shared';
import { api } from '../lib/api.js';
import { PhoneField } from '../components/CountryPhone.js';
import { SearchSelect } from '../components/SearchSelect.js';
import { asInputValue } from './fieldDisplay.js';

/**
 * Editable staff-internal inputs keyed by field id. The caller owns the value
 * map so it can be sent as `internalData` on approve.
 */
export function StaffFields({
  fields,
  values,
  disabled,
  onChange,
  countries = [],
  defaultCountryIso = 'GB',
  missing,
}: {
  fields: FieldDef[];
  values: Record<string, string>;
  disabled: boolean;
  onChange: (id: string, value: string) => void;
  countries?: Country[];
  defaultCountryIso?: string;
  /** Required questions an approve was refused for (F10): "Needed before you approve." */
  missing?: ReadonlySet<string>;
}) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14, marginTop: 14 }}>
      {fields.map((f) => {
        const span = f.type === 'long_text' || f.type === 'address';
        return (
          <div key={f.id} style={{ gridColumn: span ? '1 / -1' : 'auto' }}>
            <label className="field-label">
              {f.label}
              {f.required && !disabled && (
                <span aria-hidden style={{ color: 'var(--st-declined)' }}>
                  {' '}
                  *
                </span>
              )}
            </label>
            <StaffFieldInput
              field={f}
              value={values[f.id] ?? ''}
              disabled={disabled}
              onChange={(v) => onChange(f.id, v)}
              countries={countries}
              defaultCountryIso={defaultCountryIso}
            />
            {missing?.has(f.id) ? (
              <div className="field-help" role="alert" style={{ color: 'var(--st-declined)', fontWeight: 600 }}>
                Needed before you approve.
              </div>
            ) : (
              f.help && <div className="field-help">{f.help}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function StaffFieldInput({
  field: f,
  value,
  disabled,
  onChange,
  countries,
  defaultCountryIso,
}: {
  field: FieldDef;
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
  countries: Country[];
  defaultCountryIso: string;
}) {
  if (f.type === 'phone') {
    return (
      <PhoneField
        value={value}
        onChange={onChange}
        countries={countries}
        defaultIso={defaultCountryIso}
        disabled={disabled}
        placeholder={f.placeholder ?? f.label}
      />
    );
  }
  if (f.type === 'select' || f.type === 'radio') {
    return (
      <StaffSelect field={f} value={value} disabled={disabled} onChange={onChange} />
    );
  }
  if (f.type === 'long_text') {
    return (
      <textarea
        className="textarea"
        rows={2}
        disabled={disabled}
        value={value}
        placeholder={f.placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  return (
    <input
      className="input"
      type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text'}
      disabled={disabled}
      value={value}
      placeholder={f.placeholder ?? f.label}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

/**
 * Staff select/radio rendered as a dropdown. Options come from an iQ lookup
 * when `optionsSource` is set (fetched once on mount, cached in state), else
 * from the field's static value/label options.
 */
function StaffSelect({
  field: f,
  value,
  disabled,
  onChange,
}: {
  field: FieldDef;
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  const iqKey = f.optionsSource?.type === 'iq' ? f.optionsSource.key : null;
  const [iqOptions, setIqOptions] = useState<FieldOption[] | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!iqKey) return;
    let cancelled = false;
    setLoading(true);
    api
      .getLookup(iqKey)
      .then((r) => {
        if (!cancelled) setIqOptions(r.options);
      })
      .catch(() => {
        if (!cancelled) setIqOptions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [iqKey]);

  // Prefer iQ options when present; fall back to static if iQ returns none.
  const options = iqOptions && iqOptions.length ? iqOptions : resolveStaticOptions(f);

  // iQ-backed lists are searchable (they can be long); static lists stay a select.
  if (iqKey) {
    return (
      <SearchSelect
        value={value}
        options={options}
        disabled={disabled}
        onChange={onChange}
        placeholder={loading ? 'loading…' : f.placeholder ?? 'Select…'}
      />
    );
  }

  return (
    <select
      className="select"
      disabled={disabled}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">{loading ? 'loading…' : f.placeholder ?? 'Select…'}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

/** Seed a values map from a submission's stored data, falling back to each
 * field's (possibly conditional) default when no value is stored yet. `values`
 * should include all submission data so conditional defaults can read their
 * driver field (e.g. Net Purchases Account ← Supplier Type). */
export function seedStaffValues(
  fields: FieldDef[],
  values: Record<string, unknown>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of fields) {
    const raw = values[f.id];
    const value =
      raw === undefined || raw === null || raw === '' ? resolveFieldDefault(f, values) ?? '' : raw;
    out[f.id] = asInputValue(value);
  }
  return out;
}
